import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getLocations = createAsyncThunk(
  "Location/get",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.LOCATION.SLUG}?${queryString}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAccessibleLocations = createAsyncThunk(
  "Location/getAccessibleLocations",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.LOCATION.ACCESSIBLE_LOCATIONS);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Lookup variant — hits the permission-free /lookup endpoint. Used by dropdowns
// on screens of other modules; the Location management screens keep using
// getAccessibleLocations.
export const getAccessibleLocationsLookup = createAsyncThunk(
  "Location/getAccessibleLocationsLookup",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.LOCATION.ACCESSIBLE_LOCATIONS_LOOKUP
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getActiveLocations = createAsyncThunk(
  "Location/getActiveLocations",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.LOCATION.ACTIVE_LOCATIONS}?${queryString}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Lookup variant — hits the permission-free /lookup endpoint. Used by dropdowns
// on screens of other modules; the Location management screens keep using
// getActiveLocations.
export const getActiveLocationsLookup = createAsyncThunk(
  "Location/getActiveLocationsLookup",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.LOCATION.ACTIVE_LOCATIONS_LOOKUP}?${queryString}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDeliveryAddresses = createAsyncThunk(
  "Location/getDeliveryAddresses",
  async (locationId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.LOCATION.DELIVERY_ADDRESSES_LOOKUP(locationId)
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Returns the tenant-wide default Location or null. Used by Location and Warehouse
// forms to show the "another default exists" confirmation / cross-location block.
// Dropdown-style read — points at the permission-free /lookup endpoint.
export const getDefaultLocation = createAsyncThunk(
  "Location/getDefault",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.LOCATION.DEFAULT_LOOKUP);
      return data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createLocation = createAsyncThunk(
  "Location/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL.LOCATION.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteLocation = createAsyncThunk(
  "Location/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.LOCATION.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const editLocation = createAsyncThunk(
  "Location/put",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.LOCATION.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const toggleLocationStatus = createAsyncThunk(
  "Location/toggleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.LOCATION.TOGGLE_STATUS(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Normalizes the various shapes the location list APIs may return.
const extractLocationList = (payload) => {
  if (payload?.data?.list && Array.isArray(payload.data.list)) {
    return payload.data.list;
  }
  if (payload?.list && Array.isArray(payload.list)) {
    return payload.list;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  return [];
};

const locationSlice = createSlice({
  name: "location",
  initialState: {
    location_list: [],
    business_location_list: [],
    delivery_address_list: [],
    current_values: null,
    active_payload: null,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.location_list = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLocations.fulfilled, (state, action) => {
      state.location_list = action.payload.data.list;
    });
    builder
      .addCase(getAccessibleLocations.fulfilled, (state, action) => {
        // AccessibleLocations API returns data as an array - handle different possible structures
        if (
          action.payload?.data?.list &&
          Array.isArray(action.payload.data.list)
        ) {
          state.business_location_list = action.payload.data.list;
        } else if (action.payload?.list && Array.isArray(action.payload.list)) {
          state.business_location_list = action.payload.list;
        } else if (Array.isArray(action.payload?.data)) {
          state.business_location_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.business_location_list = action.payload;
        } else {
          state.business_location_list = [];
        }
      })
      .addCase(getAccessibleLocations.rejected, (state, action) => {
        state.business_location_list = [];
      })
      .addCase(getAccessibleLocationsLookup.fulfilled, (state, action) => {
        state.business_location_list = extractLocationList(action.payload);
      })
      .addCase(getAccessibleLocationsLookup.rejected, (state, action) => {
        state.business_location_list = [];
      })
      .addCase(getActiveLocations.fulfilled, (state, action) => {
        // ActiveLocations API returns all active locations - handle different possible structures
        if (
          action.payload?.data?.list &&
          Array.isArray(action.payload.data.list)
        ) {
          state.business_location_list = action.payload.data.list;
        } else if (action.payload?.list && Array.isArray(action.payload.list)) {
          state.business_location_list = action.payload.list;
        } else if (Array.isArray(action.payload?.data)) {
          state.business_location_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.business_location_list = action.payload;
        } else {
          state.business_location_list = [];
        }
      })
      .addCase(getActiveLocations.rejected, (state, action) => {
        state.business_location_list = [];
      })
      .addCase(getActiveLocationsLookup.fulfilled, (state, action) => {
        state.business_location_list = extractLocationList(action.payload);
      })
      .addCase(getActiveLocationsLookup.rejected, (state, action) => {
        state.business_location_list = [];
      })
      .addCase(getDeliveryAddresses.fulfilled, (state, action) => {
        // DeliveryAddresses API returns business location + its warehouses
        if (
          action.payload?.data?.list &&
          Array.isArray(action.payload.data.list)
        ) {
          state.delivery_address_list = action.payload.data.list;
        } else if (action.payload?.list && Array.isArray(action.payload.list)) {
          state.delivery_address_list = action.payload.list;
        } else if (Array.isArray(action.payload?.data)) {
          state.delivery_address_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.delivery_address_list = action.payload;
        } else {
          state.delivery_address_list = [];
        }
      })
      .addCase(getDeliveryAddresses.rejected, (state, action) => {
        state.delivery_address_list = [];
      });
  },
});

export const { setCurrentValues, setActivePayload, resetCurrentValue } =
  locationSlice.actions;
export default locationSlice.reducer;
